import { useState, useEffect } from 'react';
import moment from 'moment';
import { AlertType } from '../Models/Alert';
import { v4 as uuidv4 } from 'uuid';
import { showAlert, ShowAlertAction } from '../Store/Notifications/actions';
import { isNil, isEmpty } from 'ramda';
import { IPrincipalPermission, PermissionSet, User } from '../Models/User';
import { authService } from '..';
import { httpGetAsync } from '../Http/HttpClient';
import { IPerformanceReport } from '../Models/PerformanceReport';
import { DealerReport, IMonthYear } from '../Models/DealerReport';
import { ReportingPeriod } from '../Models/ReportingPeriod';
import { permissions } from './Constants';
export const noop = () => { };


export const formatDateStringToShortDateString = (dateString: string): string => {
  const date = moment(dateString);
  return date.format('MMM Do YY');
};

export const formatDateStringToShortDateTimeString = (dateString: string): string => {
  const date = moment(dateString);
  return date.format('MMMM Do, YYYY hh:mm A');
};

export const displayError = (error: (string | { message: string })): ShowAlertAction => {
  let message = '';
  if (typeof error !== 'string') {
    message = error.message;
  } else {
    message = error;
  }

  return displayMessage(message, 'error');
};

export const displayMessage = (message: string, type: AlertType): ShowAlertAction => {
  return showAlert({
    type: type,
    message,
    id: uuidv4(),
  });
};

export const compareDates = (dateString1: string, dateString2: string): number => {
  const date1 = moment(dateString1);
  const date2 = moment(dateString2);
  return date1 < date2 ? 1 : date2 < date1 ? -1 : 0;
};

/**
 *
 * @param value
 * @returns false if value is '' or null or undefined
 */
export const hasValue = (value: string | number | undefined | null) => {
  if (isNil(value)) {
    return false;
  } else if (typeof value !== 'number' && isEmpty(value.trim())) {
    return false;
  }
  return true;
};

/**
 * Capitalizes the first charactor of the given string.
 * @param string The string to capitalize the first letter for.
 * @returns
 */
export const capitalizeFirstLetter = (item?: string): string => {
  if (hasValue(item)) {
    return item!.charAt(0).toUpperCase() + item!.slice(1);
  } else {
    return '';
  }
};

/**
 * Gets the width and heigh of the current browser.
 * @returns 
 */
const getBrowserWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export const useBrowserWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(getBrowserWindowDimensions());
  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getBrowserWindowDimensions());
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

export const nullOrNumber = (val: string): number | null => {
  if (val.length === 0) {
    return null;
  }

  const ret = parseFloat(val);
  if (ret === undefined) {
    return null;
  }

  return ret;
};

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent'
});

export const toNumber = (number: number, decimalPlaces: number = 0) => {
  return Number.parseFloat(number.toFixed(decimalPlaces));
};

export const userHasPermission = (user: User, permission: string, permissionType: PermissionSet): boolean => {
  const userPermission: IPrincipalPermission | undefined = user.permissions?.find(
    p => p.id.toLowerCase() == permission.toLowerCase());

  if (userPermission) {
    switch (permissionType) {
      case PermissionSet.C:
        if (userPermission.canCreate === false) {
          return false;
        }
        break;
      case PermissionSet.R:
        if (userPermission.canView === false) {
          return false;
        }
        break;
      case PermissionSet.U:
        if (userPermission.canUpdate === false) {
          return false;
        }
        break;
      case PermissionSet.D:
        if (userPermission.canDelete === false) {
          return false;
        }
        break;
      default:
        return false;
    }
    return true;
  }
  return false;
};

export const ShowModifiedDateWarning = async (performanceReport: IPerformanceReport) => {
  const token: string = await authService.getToken();
  const bearer = `Bearer ${token}`;
  const response = await httpGetAsync(`${authService.appSettings.GetWebApiBaseUri()}/PerformanceReport/getPerformanceReportLastSaveDate/${performanceReport.id}`, token);

  if (response && response.ok) {
    const responseValue = await response.json();

    if (!responseValue) {
      return false;
    }

    if (responseValue.lastModifiedDate > (performanceReport.modifiedDate ?? responseValue.lastModifiedDate)) {
      return true;
    }
  }

  return false;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = (match[1] ? '+1 ' : '');
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return '';
};

export function getMissingReportingPeriods(dealerReports: DealerReport[], businessTypeId: string): ReportingPeriod[] {
  // Initialize the start date and the current date
  const startDate = new Date(2023, 0); // January 2023
  const currentDate = new Date();

  // Create a set of reporting periods from the dealerReports array
  const reportPeriods = new Set(dealerReports.filter(dr => dr.businessTypeId.toLowerCase() === businessTypeId.toLowerCase())
    .map(report => `${report.year}-${report.month}`));

  // Initialize the array of missing reporting periods
  let missingPeriods = [];

  // Loop from the start date to the current date
  for (let d = startDate; d <= currentDate; d.setMonth(d.getMonth() + 1)) {
    let year = d.getFullYear();
    let month = d.getMonth() + 1; // JavaScript months are 0-based

    // If this reporting period is not in the set, add it to the missing periods
    if (!reportPeriods.has(`${year}-${month}`)) {
      missingPeriods.push({ month, year, businessTypeId });
    }
  }

  return missingPeriods;
}

export function userCanSwitchClients(user?: User | null) {
  return user && (user.isSuperUser === true || userHasPermission(user, permissions.clients, PermissionSet.U))
}

export function getPreviousSixMonths(): string[] {
  const previousThreeMonths = [
    moment().subtract(6, 'months').format('MMM'),
    moment().subtract(5, 'months').format('MMM'),
    moment().subtract(4, 'months').format('MMM'),
    moment().subtract(3, 'months').format('MMM'),
    moment().subtract(2, 'months').format('MMM'),
    moment().subtract(1, 'months').format('MMM')
  ];

  return previousThreeMonths;
}

export function sortByYearThenMonthDescending(left: IMonthYear, right: IMonthYear): number {
  if (left.year === right.year) {
    return right.month - left.month;
  }
  return right.year - left.year;
}

export const formatMonthYear = (month: number, year: number): string => {
  const formattedMonth = month.toString().padStart(2, '0');
  return `${year}-${formattedMonth}`;
}

export const isInRole = (roles: string[], roleId: string): boolean => {
  return roles.map(r => r.toLowerCase()).includes(roleId.toLowerCase());
}